import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LayoutDashboard, MapPin, Tag, CalendarDays, LogOut, Search, Rocket, Package, Users, Code2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import CitiesAdmin from "@/components/admin/CitiesAdmin";
import CategoriesAdmin from "@/components/admin/CategoriesAdmin";
import EventsAdmin from "@/components/admin/EventsAdmin";
import SponsorsAdmin from "@/components/admin/SponsorsAdmin";
import PackagesAdmin from "@/components/admin/PackagesAdmin";
import UsersAdmin from "@/components/admin/UsersAdmin";
import ScriptsAdmin from "@/components/admin/ScriptsAdmin";

const TABS = [
  { key: "events", name: "Events", icon: CalendarDays },
  { key: "sponsors", name: "Sponsors", icon: Rocket },
  { key: "packages", name: "Packages", icon: Package },
  { key: "users", name: "Users", icon: Users },
  { key: "cities", name: "Cities", icon: MapPin },
  { key: "categories", name: "Categories", icon: Tag },
  { key: "scripts", name: "Scripts", icon: Code2 },
];

export default function AdminDashboard() {
  const { user, loadingUser, logout } = useApp();
  const [tab, setTab] = useState("events");

  if (loadingUser) return <div className="p-10 text-gray-500">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-[#F0EFEA] flex">
      <aside data-testid="admin-sidebar" className="w-64 bg-[#1A1A1A] text-white fixed h-full flex flex-col">
        <Link to="/" className="px-6 py-6 border-b border-white/10">
          <div className="font-display text-xl font-black">Fanp<span className="text-[#FA003F]">i</span>t.Cl<span className="text-[#FA003F]">u</span>b</div>
          <div className="text-xs text-white/50 mt-0.5">Admin Console</div>
        </Link>
        <nav className="flex-1 py-4 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`w-full px-6 py-3 flex items-center gap-3 text-sm font-semibold transition-colors ${
                tab === t.key
                  ? "bg-[#FA003F] text-white"
                  : "text-white/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.name}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/50">Signed in as</div>
          <div className="font-bold text-sm truncate">{user.email}</div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="mt-3 w-full h-10 rounded-full bg-white/10 hover:bg-[#FA003F] text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      <main className="ml-64 flex-1 p-8">
        <div className="max-w-6xl">
          {tab === "events" && <EventsAdmin />}
          {tab === "sponsors" && <SponsorsAdmin />}
          {tab === "packages" && <PackagesAdmin />}
          {tab === "users" && <UsersAdmin />}
          {tab === "cities" && <CitiesAdmin />}
          {tab === "categories" && <CategoriesAdmin />}
          {tab === "scripts" && <ScriptsAdmin />}
        </div>
      </main>
    </div>
  );
}
